import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Collapse,
  Empty,
  InputNumber,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  announceAll,
  announceNominees,
  announceWinners,
  deleteNominee,
  listNominees,
  unannounceNominees,
  unannounceWinners,
  updateNominee,
} from '../api';
import NomineeModal from '../components/nominee-modal';
import NomineePasteModal from '../components/nominee-paste-modal';
import {
  AnnounceStage,
  GalaEventStatus,
  GalaNominee,
  NOMINEE_RESULT_META,
  NomineeCategory,
  NomineeMember,
  NomineeResult,
  NomineeUpdateInput,
  errorText,
} from '../contract';
import { fileUrl } from '../lib/file-url';
import {
  announceRecipients,
  canAnnounce,
  nominationStatusLabel,
  winnerPushTitle,
} from '../lib/nomination-status';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
  eventStatus: GalaEventStatus;
}

const RESULT_OPTIONS = (Object.keys(NOMINEE_RESULT_META) as NomineeResult[]).map((value) => ({
  value,
  label: NOMINEE_RESULT_META[value].text,
}));

/** Chip một vé thành viên; cảnh báo khi vé đã thu hồi hoặc chưa gắn tài khoản (người đó chưa thấy trong app). */
const MemberChip = ({ m }: { m: NomineeMember }) => {
  const name = m.username ? `${m.displayName} (@${m.username})` : m.displayName;
  if (m.status === 'REVOKED') {
    return (
      <Tooltip title="Vé đã thu hồi: người này bị bỏ qua khi công bố.">
        <Tag color="red" className={styles.memberRevoked}>
          {name} · vé đã thu hồi
        </Tag>
      </Tooltip>
    );
  }
  if (!m.hasAccount) {
    return (
      <Tooltip title="Chưa gắn tài khoản: người này chưa thấy đề cử trong app và không nhận thông báo.">
        <Tag color="orange">{name} · chưa gắn tài khoản</Tag>
      </Tooltip>
    );
  }
  return <Tag>{name}</Tag>;
};

/** Hộp xác nhận người thắng sau khi hạng mục đã công bố người thắng (I2). */
type WinnerDraft = {
  cat: NomineeCategory;
  nominee: GalaNominee;
  /** PUSH: đổi sang Người thắng → push "Chúc mừng" ngay; EDIT: sửa hạng / giải của người thắng. */
  mode: 'PUSH' | 'EDIT';
  rank: number | null;
  prize: string;
};

/**
 * Ô kết quả sửa trực tiếp: Select lưu ngay, hạng và giải lưu khi rời ô / bấm Enter.
 * Hạng mục đã công bố người thắng: chọn "Người thắng" hoặc sửa giải của người thắng
 * đi qua hộp xác nhận (`onAskWinner`) để gửi kết quả + hạng + giải trong MỘT lần lưu.
 */
const ResultEditor = ({
  nominee,
  disabled,
  winnersOut,
  onSave,
  onAskWinner,
}: {
  nominee: GalaNominee;
  disabled: boolean;
  winnersOut: boolean;
  onSave: (patch: NomineeUpdateInput) => void;
  onAskWinner: (mode: 'PUSH' | 'EDIT', rank: number | null, prize: string) => void;
}) => {
  const [rank, setRank] = useState<number | null>(nominee.rank);
  const [prize, setPrize] = useState(nominee.prizeLabel ?? '');
  // Người thắng đã công bố: hạng / giải chỉ sửa qua hộp xác nhận.
  const locked = winnersOut && nominee.result === 'WINNER';

  // Dữ liệu tải lại từ server (hoặc đổi sang dòng khác): đồng bộ lại ô đang gõ.
  useEffect(() => {
    setRank(nominee.rank);
    setPrize(nominee.prizeLabel ?? '');
  }, [nominee.id, nominee.rank, nominee.prizeLabel]);

  const commitRank = () => {
    if (rank !== nominee.rank) onSave({ rank });
  };
  const commitPrize = () => {
    const next = prize.trim() || null;
    if (next !== nominee.prizeLabel) onSave({ prizeLabel: next });
  };

  return (
    <div className={styles.resultCell}>
      <Select<NomineeResult>
        size="small"
        style={{ width: 150 }}
        popupMatchSelectWidth={false}
        value={nominee.result}
        options={RESULT_OPTIONS}
        disabled={disabled}
        aria-label={`Kết quả của ${nominee.displayName}`}
        onChange={(result) => {
          if (winnersOut && result === 'WINNER' && nominee.result !== 'WINNER') {
            onAskWinner('PUSH', rank, prize);
          } else {
            onSave({ result });
          }
        }}
      />
      <InputNumber
        size="small"
        min={1}
        precision={0}
        style={{ width: 64 }}
        placeholder="Hạng"
        value={rank}
        disabled={disabled || locked}
        aria-label={`Hạng của ${nominee.displayName}`}
        onChange={(v) => setRank(typeof v === 'number' ? v : null)}
        onBlur={commitRank}
        onPressEnter={commitRank}
      />
      <Input
        size="small"
        style={{ width: 150 }}
        placeholder="Tên giải (Vàng, Top 1…)"
        maxLength={80}
        value={prize}
        disabled={disabled || locked}
        aria-label={`Tên giải của ${nominee.displayName}`}
        onChange={(e) => setPrize(e.target.value)}
        onBlur={commitPrize}
        onPressEnter={commitPrize}
      />
      {locked && (
        <Button
          type="link"
          size="small"
          disabled={disabled}
          onClick={() => onAskWinner('EDIT', nominee.rank, nominee.prizeLabel ?? '')}
        >
          Sửa giải
        </Button>
      )}
    </div>
  );
};

/** Tab "Đề cử & giải": đề cử theo hạng mục, kết quả, công bố / huỷ công bố (SPEC §5). */
const NominationsTab = ({ eventId, eventStatus }: Props) => {
  const { message } = App.useApp();
  const [categories, setCategories] = useState<NomineeCategory[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ categoryId: number; nominee: GalaNominee | null } | null>(
    null
  );
  const [pasting, setPasting] = useState<NomineeCategory | null>(null);
  const [winnerDraft, setWinnerDraft] = useState<WinnerDraft | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(
    async (silent = true) => {
      const ticket = ++requestRef.current;
      if (!silent) setLoading(true);
      try {
        const res = await listNominees(eventId);
        if (ticket !== requestRef.current) return;
        setCategories(res?.categories ?? []);
        setFailed(null);
      } catch (e) {
        if (ticket !== requestRef.current) return;
        if (silent) message.error(errorText(e, 'Không tải lại được danh sách đề cử.'));
        else setFailed(errorText(e, 'Không tải được danh sách đề cử.'));
      } finally {
        if (ticket === requestRef.current) setLoading(false);
      }
    },
    [eventId, message]
  );

  useEffect(() => {
    load(false);
    return () => {
      requestRef.current += 1; // bỏ kết quả đang bay khi đổi sự kiện / rời tab
    };
  }, [load]);

  /** Chạy một thao tác, báo lỗi, tải lại im lặng. `key` khoá các nút trong lúc chạy. */
  const run = async (key: string, task: () => Promise<unknown>, fail: string) => {
    setBusy(key);
    try {
      await task();
      await load(true);
    } catch (e) {
      message.error(errorText(e, fail));
    } finally {
      setBusy(null);
    }
  };

  // Đề cử chỉ công bố khi PUBLISHED; người thắng công bố được cả khi đã ENDED (sau đêm Gala).
  const canNominees = canAnnounce('NOMINEES', eventStatus);
  const canWinners = canAnnounce('WINNERS', eventStatus);

  const doAnnounce = (cat: NomineeCategory, stage: AnnounceStage) =>
    run(
      `${stage}-${cat.id}`,
      async () => {
        const res =
          stage === 'NOMINEES'
            ? await announceNominees(eventId, cat.id)
            : await announceWinners(eventId, cat.id);
        message.success(
          `Đã công bố ${stage === 'NOMINEES' ? 'đề cử' : 'người thắng'} hạng mục ${cat.code}. Đã gửi thông báo cho ${
            res?.pushed ?? 0
          } người.`
        );
      },
      stage === 'NOMINEES' ? 'Không công bố được đề cử.' : 'Không công bố được người thắng.'
    );

  const doUnannounce = (cat: NomineeCategory, stage: AnnounceStage) =>
    run(
      `UN${stage}-${cat.id}`,
      async () => {
        if (stage === 'NOMINEES') await unannounceNominees(eventId, cat.id);
        else await unannounceWinners(eventId, cat.id);
        message.success(`Đã huỷ công bố ${stage === 'NOMINEES' ? 'đề cử' : 'người thắng'}.`);
      },
      'Không huỷ công bố được.'
    );

  const doAnnounceAll = (stage: AnnounceStage) =>
    run(
      `ALL-${stage}`,
      async () => {
        const res = await announceAll(eventId, stage);
        const done = res?.done?.length ?? 0;
        // Chỉ báo hạng mục admin có thể đã muốn công bố: bỏ hạng mục trống và hạng mục đã công bố từ trước.
        const skipped = (res?.skipped ?? []).filter((s) => {
          const cat = categories?.find((c) => c.id === s.categoryId);
          return !!cat && cat.nominees.length > 0 && !s.reason.startsWith('Đã công bố');
        });
        message.success(
          typeof res?.pushed === 'number'
            ? `Đã công bố ${done} hạng mục. Đã gửi thông báo cho ${res.pushed} người.`
            : `Đã công bố ${done} hạng mục.`
        );
        if (skipped.length) {
          Modal.info({
            title: `${skipped.length} hạng mục bị bỏ qua`,
            content: (
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {skipped.map((s) => {
                  const cat = categories?.find((c) => c.id === s.categoryId);
                  return (
                    <li key={s.categoryId}>
                      <strong>{cat ? `${cat.code} — ${cat.nameVi}` : `#${s.categoryId}`}</strong>:{' '}
                      {s.reason}
                    </li>
                  );
                })}
              </ul>
            ),
          });
        }
      },
      'Không công bố được hàng loạt.'
    );

  const saveResult = (cat: NomineeCategory, n: GalaNominee, patch: NomineeUpdateInput) =>
    run(
      `RES-${n.id}`,
      async () => {
        await updateNominee(eventId, n.id, patch);
        message.success(
          cat.winnersAnnouncedAt
            ? 'Đã lưu. Người thắng đã công bố nên thay đổi hiện ngay trong app.'
            : 'Đã lưu kết quả.'
        );
      },
      'Không lưu được kết quả.'
    );

  /** Lưu hộp xác nhận người thắng: kết quả + hạng + giải trong MỘT lần PUT (server push sau khi lưu đủ). */
  const saveWinnerDraft = async (d: WinnerDraft) => {
    const patch: NomineeUpdateInput = { rank: d.rank, prizeLabel: d.prize.trim() || null };
    if (d.mode === 'PUSH') patch.result = 'WINNER';
    setWinnerDraft(null);
    await run(
      `RES-${d.nominee.id}`,
      async () => {
        await updateNominee(eventId, d.nominee.id, patch);
        message.success(
          d.mode === 'PUSH'
            ? 'Đã lưu Người thắng và gửi thông báo chúc mừng.'
            : 'Đã lưu. Tên giải / hạng mới hiện ngay trong app.'
        );
      },
      'Không lưu được kết quả.'
    );
  };

  const remove = (cat: NomineeCategory, n: GalaNominee) =>
    run(
      `DEL-${n.id}`,
      async () => {
        await deleteNominee(eventId, n.id);
        message.success('Đã xoá đề cử.');
      },
      'Không xoá được đề cử.'
    );

  // ---- Đếm cho các hộp xác nhận "công bố tất cả" ----
  const allStage = (stage: AnnounceStage) => {
    // Hạng mục ẩn bị server bỏ qua ("Hạng mục đang ẩn") nên không đếm.
    const eligible = (categories ?? []).filter(
      (c) =>
        !c.isHidden &&
        (stage === 'NOMINEES'
          ? !c.nomineesAnnouncedAt && c.nominees.length > 0
          : !!c.nomineesAnnouncedAt &&
            !c.winnersAnnouncedAt &&
            c.nominees.some((n) => n.result === 'WINNER'))
    );
    return {
      cats: eligible.length,
      people: eligible.reduce((sum, c) => sum + announceRecipients(c.nominees, stage), 0),
    };
  };

  if (loading && !categories) {
    return (
      <div className={styles.placeholder}>
        <Spin />
      </div>
    );
  }

  if (!categories) {
    return (
      <Alert
        type="warning"
        showIcon
        message={failed ?? 'Không tải được danh sách đề cử.'}
        action={
          <Button size="small" onClick={() => load(false)}>
            Thử lại
          </Button>
        }
      />
    );
  }

  const columns = (cat: NomineeCategory): ColumnsType<GalaNominee> => [
    {
      title: '',
      dataIndex: 'imageKey',
      width: 56,
      render: (key: string | null) =>
        key ? <img className={styles.nomineeThumb} src={fileUrl(key)} alt="" loading="lazy" /> : null,
    },
    {
      title: 'Đơn vị được đề cử',
      dataIndex: 'displayName',
      width: 200,
      render: (v: string, n) => (
        <div className={styles.titleCell}>
          <strong>{v}</strong>
          {n.note ? <span className={styles.subtle}>{n.note}</span> : null}
        </div>
      ),
    },
    {
      title: 'Thành viên (vé)',
      key: 'members',
      render: (_: unknown, n) => (
        <div className={styles.memberTags}>
          {n.members.length === 0 ? (
            <span className={styles.unlinked}>Chưa có vé thành viên</span>
          ) : (
            n.members.map((m) => <MemberChip key={m.ticketId} m={m} />)
          )}
        </div>
      ),
    },
    {
      title: 'Kết quả · hạng · giải',
      key: 'result',
      width: 490,
      render: (_: unknown, n) => (
        <ResultEditor
          nominee={n}
          disabled={busy !== null}
          winnersOut={!!cat.winnersAnnouncedAt}
          onSave={(patch) => saveResult(cat, n, patch)}
          onAskWinner={(mode, rank, prize) =>
            setWinnerDraft({ cat, nominee: n, mode, rank, prize })
          }
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_: unknown, n) => (
        <Space size={0}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing({ categoryId: cat.id, nominee: n })}
          >
            Sửa
          </Button>
          <Popconfirm
            title={`Xoá đề cử "${n.displayName}"?`}
            description={
              cat.nomineesAnnouncedAt
                ? 'Hạng mục này đã công bố đề cử: người trong đơn vị sẽ không còn thấy đề cử trong app.'
                : 'Xoá hẳn, không khôi phục được.'
            }
            okText="Xoá"
            okButtonProps={{ danger: true }}
            cancelText="Không"
            onConfirm={() => remove(cat, n)}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Xoá đề cử ${n.displayName}`}
              disabled={busy !== null}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const panel = (cat: NomineeCategory) => {
    const status = nominationStatusLabel(cat);
    const nomineesOut = !!cat.nomineesAnnouncedAt;
    const winnersOut = !!cat.winnersAnnouncedAt;
    const hasWinner = cat.nominees.some((n) => n.result === 'WINNER');
    const nomRecipients = announceRecipients(cat.nominees, 'NOMINEES');
    const winRecipients = announceRecipients(cat.nominees, 'WINNERS');
    const hidden = !!cat.isHidden;
    const HIDDEN_REASON = 'Hạng mục đang ẩn: bật hiện ở tab Hạng mục rồi mới công bố được.';
    const nomBlockReason = hidden ? HIDDEN_REASON : '';
    const winBlockReason = hidden
      ? HIDDEN_REASON
      : !nomineesOut
      ? 'Công bố đề cử của hạng mục trước.'
      : !hasWinner
      ? 'Chưa đặt đơn vị nào là Người thắng.'
      : '';

    return {
      key: String(cat.id),
      label: (
        <Space size={8} wrap>
          <strong>{cat.code}</strong>
          <span>{cat.nameVi}</span>
          {hidden && <Tag color="red">Đang ẩn</Tag>}
          <Tag>{cat.nominees.length} đơn vị</Tag>
          <Tag color={nomineesOut ? 'green' : 'default'}>Đề cử: {status.nominees}</Tag>
          <Tag color={winnersOut ? 'gold' : 'default'}>Người thắng: {status.winners}</Tag>
        </Space>
      ),
      children: (
        <div>
          <div className={styles.toolbar}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing({ categoryId: cat.id, nominee: null })}
            >
              Thêm đề cử
            </Button>
            <Button icon={<UnorderedListOutlined />} onClick={() => setPasting(cat)}>
              Dán danh sách
            </Button>
            {nomineesOut ? (
              <Popconfirm
                title="Huỷ công bố đề cử?"
                description={
                  winnersOut
                    ? 'App sẽ ẩn đề cử và cả người thắng của hạng mục này. Thông báo đã gửi thì không thu hồi được.'
                    : 'App sẽ ẩn đề cử của hạng mục này. Thông báo đã gửi thì không thu hồi được.'
                }
                okText="Huỷ công bố"
                okButtonProps={{ danger: true }}
                cancelText="Không"
                onConfirm={() => doUnannounce(cat, 'NOMINEES')}
              >
                <Button danger loading={busy === `UNNOMINEES-${cat.id}`} disabled={busy !== null}>
                  Huỷ công bố đề cử
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Công bố đề cử?"
                description={`Khoảng ${nomRecipients} người (vé đã phát, có tài khoản) sẽ nhận thông báo "Bạn được đề cử".`}
                okText="Công bố"
                cancelText="Không"
                onConfirm={() => doAnnounce(cat, 'NOMINEES')}
              >
                <Tooltip title={nomBlockReason || undefined}>
                  <span>
                    <Button
                      loading={busy === `NOMINEES-${cat.id}`}
                      disabled={
                        busy !== null ||
                        !canNominees ||
                        cat.nominees.length === 0 ||
                        !!nomBlockReason
                      }
                    >
                      Công bố đề cử
                    </Button>
                  </span>
                </Tooltip>
              </Popconfirm>
            )}
            {winnersOut ? (
              <Popconfirm
                title="Huỷ công bố người thắng?"
                description="App sẽ ẩn người thắng của hạng mục này. Thông báo đã gửi thì không thu hồi được."
                okText="Huỷ công bố"
                okButtonProps={{ danger: true }}
                cancelText="Không"
                onConfirm={() => doUnannounce(cat, 'WINNERS')}
              >
                <Button danger loading={busy === `UNWINNERS-${cat.id}`} disabled={busy !== null}>
                  Huỷ công bố người thắng
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Công bố người thắng?"
                description={`Khoảng ${winRecipients} người (vé đã phát, có tài khoản, thuộc đơn vị Người thắng) sẽ nhận thông báo "Chúc mừng". Đơn vị chưa có kết quả được tính là không thắng.`}
                okText="Công bố"
                cancelText="Không"
                onConfirm={() => doAnnounce(cat, 'WINNERS')}
              >
                <Tooltip title={winBlockReason || undefined}>
                  <span>
                    <Button
                      type="primary"
                      loading={busy === `WINNERS-${cat.id}`}
                      disabled={busy !== null || !canWinners || !!winBlockReason}
                    >
                      Công bố người thắng
                    </Button>
                  </span>
                </Tooltip>
              </Popconfirm>
            )}
          </div>

          {winnersOut && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Người thắng của hạng mục này đã công bố: sửa kết quả ở bảng dưới sẽ hiện ngay trong app. Chọn thêm Người thắng sẽ GỬI NGAY thông báo chúc mừng (không thu hồi được) — hệ thống hỏi lại kèm tên giải trước khi gửi."
            />
          )}

          <Table<GalaNominee>
            rowKey="id"
            size="small"
            columns={columns(cat)}
            dataSource={cat.nominees}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Chưa có đề cử. Bấm Thêm đề cử hoặc Dán danh sách.' }}
          />
        </div>
      ),
    };
  };

  const editingCat = editing ? categories.find((c) => c.id === editing.categoryId) : undefined;
  const nominees = allStage('NOMINEES');
  const winners = allStage('WINNERS');

  return (
    <div>
      {!canNominees && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            canWinners
              ? 'Sự kiện đã kết thúc: vẫn công bố được người thắng; công bố đề cử chỉ khi sự kiện ở trạng thái Đã công bố.'
              : 'Chỉ công bố được khi sự kiện đã ở trạng thái Đã công bố. Bạn vẫn lập đề cử và đặt kết quả trước được.'
          }
        />
      )}

      <div className={styles.toolbar}>
        <Popconfirm
          title="Công bố tất cả đề cử?"
          description={`${nominees.cats} hạng mục đủ điều kiện, khoảng ${nominees.people} lượt thông báo "Bạn được đề cử".`}
          okText="Công bố"
          cancelText="Không"
          onConfirm={() => doAnnounceAll('NOMINEES')}
        >
          <Button
            type="primary"
            loading={busy === 'ALL-NOMINEES'}
            disabled={busy !== null || !canNominees || nominees.cats === 0}
          >
            Công bố tất cả đề cử
          </Button>
        </Popconfirm>
        <Popconfirm
          title="Công bố tất cả người thắng?"
          description={`${winners.cats} hạng mục đủ điều kiện, khoảng ${winners.people} lượt thông báo "Chúc mừng".`}
          okText="Công bố"
          cancelText="Không"
          onConfirm={() => doAnnounceAll('WINNERS')}
        >
          <Button
            type="primary"
            loading={busy === 'ALL-WINNERS'}
            disabled={busy !== null || !canWinners || winners.cats === 0}
          >
            Công bố tất cả người thắng
          </Button>
        </Popconfirm>
        <Button icon={<ReloadOutlined />} onClick={() => load(false)} disabled={busy !== null}>
          Tải lại
        </Button>
      </div>

      {categories.length === 0 ? (
        <Empty description="Sự kiện chưa có hạng mục. Thêm hạng mục ở tab Hạng mục trước." />
      ) : (
        <Collapse items={categories.map(panel)} />
      )}

      <NomineeModal
        eventId={eventId}
        categoryId={editing?.categoryId ?? 0}
        nominee={editing?.nominee ?? null}
        open={!!editing && !!editingCat}
        onClose={() => setEditing(null)}
        onSaved={() => load(true)}
      />
      <Modal
        open={!!winnerDraft}
        title={
          winnerDraft?.mode === 'PUSH'
            ? `Đặt "${winnerDraft.nominee.displayName}" là Người thắng?`
            : `Sửa giải của "${winnerDraft?.nominee.displayName ?? ''}"`
        }
        okText={winnerDraft?.mode === 'PUSH' ? 'Lưu và gửi thông báo' : 'Lưu'}
        cancelText="Không"
        okButtonProps={{ loading: busy !== null }}
        onCancel={() => setWinnerDraft(null)}
        onOk={() => winnerDraft && saveWinnerDraft(winnerDraft)}
        destroyOnClose
      >
        {winnerDraft && (
          <div>
            {winnerDraft.mode === 'PUSH' ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Sẽ gửi thông báo chúc mừng ngay cho ${announceRecipients(
                  [{ ...winnerDraft.nominee, result: 'WINNER' }],
                  'WINNERS'
                )} người (vé đã phát, có tài khoản). Thông báo đã gửi thì không thu hồi được — điền tên giải trước khi lưu.`}
              />
            ) : (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Thông báo chúc mừng đã gửi trước đó không gửi lại; hạng và tên giải mới hiện ngay trong app."
              />
            )}
            <Space wrap>
              <InputNumber
                min={1}
                precision={0}
                style={{ width: 90 }}
                placeholder="Hạng"
                value={winnerDraft.rank}
                aria-label="Hạng"
                onChange={(v) =>
                  setWinnerDraft({ ...winnerDraft, rank: typeof v === 'number' ? v : null })
                }
              />
              <Input
                style={{ width: 240 }}
                placeholder="Tên giải (Vàng, Top 1…)"
                maxLength={80}
                value={winnerDraft.prize}
                aria-label="Tên giải"
                onChange={(e) => setWinnerDraft({ ...winnerDraft, prize: e.target.value })}
              />
            </Space>
            {winnerDraft.mode === 'PUSH' && (
              <p className={styles.subtle} style={{ marginTop: 12, marginBottom: 0 }}>
                Nội dung thông báo:{' '}
                <strong>{winnerPushTitle(winnerDraft.prize, winnerDraft.cat.nameVi)}</strong>
              </p>
            )}
          </div>
        )}
      </Modal>
      <NomineePasteModal
        eventId={eventId}
        categoryId={pasting?.id ?? 0}
        categoryName={pasting ? `${pasting.code} ${pasting.nameVi}` : ''}
        open={!!pasting}
        onClose={() => setPasting(null)}
        onDone={() => load(true)}
      />
    </div>
  );
};

export default NominationsTab;
